// Strategy NAV over time.
//
// Plots `strategyNavUsd` per snapshot — LP value + pending fees +
// pending AERO + HL perp account + HL spot. Pending AERO inclusion
// added later to smooth harvest-day "jumps" in the curve (see
// observer.ts: the pre-AERO formula caused a visible step-down each
// time the harvester fired). The reference line is the user's
// declared `deployed_capital_usd`, so the visual delta is the actual
// strategy alpha (not polluted by wallet ETH price drift).
//
// Legacy `navUsd` (total wallet) still flows through the snapshots
// endpoint; a separate "Wallet over time" chart can surface it in a
// later phase if useful.
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../layout/theme-provider';
import { useSnapshots } from '../../hooks/useApi';
import {
  formatCurrency,
  formatDateShort,
  formatDateTime,
  formatTimeShort,
} from '../../lib/utils';

/** Bucket width for downsampling, by selected range, so the line stays
 *  readable: 15-min within 24h, coarser as the window grows. */
function bucketMsForHours(hours: number): number {
  if (hours <= 24) return 15 * 60_000; // 24h → 15-min
  if (hours <= 24 * 7) return 60 * 60_000; // 7d → hourly
  if (hours <= 24 * 30) return 6 * 60 * 60_000; // 30d → 6-hourly
  return 24 * 60 * 60_000; // All → daily
}

/** Collapse the raw ~30s snapshots into one point per bucket (the latest NAV
 *  in each bucket), so 24h isn't a 30s-jagged line and longer ranges don't
 *  plot thousands of points. */
function bucketPoints(
  points: { ts: number; nav: number }[],
  bucketMs: number,
): { ts: number; nav: number }[] {
  const sorted = [...points].sort((a, b) => a.ts - b.ts);
  const byBucket = new Map<number, { ts: number; nav: number }>();
  for (const p of sorted) byBucket.set(Math.floor(p.ts / bucketMs), p);
  return [...byBucket.values()];
}

interface Props {
  /** Reference line value — strategy.deployedCapitalUsd from /api/state. */
  baselineUsd: number | null;
  /** Label for the reference line. Defaults to "deployed". */
  baselineLabel?: string;
  hours?: number;
  height?: number;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: number;
}) {
  if (!active || !payload?.length || label === undefined) return null;
  return (
    <div className="rounded-lg border border-border bg-surface shadow-lg p-3 text-sm">
      <p className="text-muted-foreground mb-1">{formatDateTime(label)}</p>
      <p className="font-semibold text-foreground tabular-nums">
        NAV: {formatCurrency(payload[0]!.value)}
      </p>
    </div>
  );
}

export function NavAreaChart({
  baselineUsd,
  baselineLabel = 'deployed',
  hours = 24,
  height = 240,
}: Props) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const gridColor = isDark ? 'hsl(215 25% 27%)' : 'hsl(214 32% 91%)';
  const textColor = isDark ? 'hsl(215 20% 65%)' : 'hsl(215 16% 47%)';
  const areaColor = 'hsl(160 84% 39%)'; // emerald — growth-positive
  const baselineColor = isDark ? 'hsl(215 20% 55%)' : 'hsl(215 16% 60%)';

  const { data: snapshots } = useSnapshots(hours);
  // Strategy NAV only — no fallback to navUsd. The observer writes
  // strategyNavUsd=null until the strategy owns an LP position; falling
  // back to navUsd (whole wallet) would render a misleading curve
  // (idle capital + HL spot dust) before the strategy has actually
  // deployed. Pre-Phase-3 snapshots without strategyNavUsd are filtered
  // out — acceptable, we didn't track per-strategy NAV then.
  const points = (snapshots ?? [])
    .map((s) => {
      const nav = s.strategyNavUsd;
      return nav !== null && nav !== undefined ? { ts: s.ts, nav } : null;
    })
    .filter((p): p is { ts: number; nav: number } => p !== null);

  // Range-aware bucketing + axis: tight time buckets within a day, calendar
  // dates for multi-day windows so "7d"/"30d" read as dates, not clock times.
  const intraday = hours <= 24;
  const bucketed = bucketPoints(points, bucketMsForHours(hours));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={bucketed} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="navGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={areaColor} stopOpacity={0.18} />
            <stop offset="95%" stopColor={areaColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
        <XAxis
          dataKey="ts"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={intraday ? formatTimeShort : formatDateShort}
          tick={{ fill: textColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          minTickGap={48}
        />
        <YAxis
          tick={{ fill: textColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => `$${v.toFixed(0)}`}
          width={56}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: gridColor }} />
        {baselineUsd !== null && (
          <ReferenceLine
            y={baselineUsd}
            stroke={baselineColor}
            strokeDasharray="4 4"
            label={{
              value: `${baselineLabel} ${formatCurrency(baselineUsd)}`,
              position: 'insideTopRight',
              fontSize: 10,
              fill: baselineColor,
            }}
          />
        )}
        <Area
          type="monotone"
          dataKey="nav"
          stroke={areaColor}
          strokeWidth={2}
          fill="url(#navGradient)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
